import type { HomepagePromotionPricing } from "@/hooks/useHomepagePromotionPricing";
import type { HomepagePromoQuote } from "@/hooks/useHomepagePromoQuote";
import { formatCents } from "@/components/tickets/TicketPurchaseParts";

const PRICE_STYLES = `
  .homepage-event-price{display:flex;flex-wrap:wrap;align-items:baseline;gap:.45rem .7rem;margin:1rem 0;color:#F7F3F7;font-family:'Space Grotesk',sans-serif}
  .homepage-event-price strong{font-size:clamp(1.2rem,2.2vw,1.65rem);font-weight:700;letter-spacing:-.025em}
  .homepage-event-price s{color:#B8ADBA;font-size:.86rem;text-decoration-thickness:1px}
  .homepage-event-price span{color:#E9C6D8;font-size:.76rem}
  .homepage-event-price--compact{margin:0;gap:.35rem .55rem}
  .homepage-event-price--compact strong{font-size:1.05rem}
  .homepage-event-price--compact s{font-size:.75rem}
  .homepage-event-price--compact span{font-size:.68rem}
  .homepage-event-price--lny{justify-content:center;margin:1.5rem 0 0;color:var(--lny-gold-bright,#F6E27A);font-size:clamp(1.05rem,2.1vw,1.4rem);font-weight:500;letter-spacing:.04em}
  .homepage-event-price--lny strong{font-family:var(--lny-serif,'Cormorant Garamond',serif);font-size:clamp(2rem,4.4vw,3.1rem);line-height:1;letter-spacing:.01em;background:linear-gradient(180deg,#FFF8D2,#F6E27A 45%,#E8C547);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
  .homepage-event-price--lny s{color:rgba(246,226,122,.5);font-size:.78em}
  .homepage-event-price--lny span{width:100%;color:rgba(246,226,122,.8);font-size:.62em;letter-spacing:.1em;text-transform:uppercase}
  @media(max-width:560px){.homepage-event-price--compact{align-items:center}}
`;

/**
 * The single homepage price presentation: exact public policy label for free and
 * external registration, and only a current, available ticketQuote for internal.
 */
export default function FeaturedEventPrice({
  pricing,
  className = "",
}: {
  pricing: {
    policy: HomepagePromotionPricing | null;
    quote: HomepagePromoQuote | null;
    quoteUsable: boolean;
    promoActive: boolean;
  };
  className?: string;
}) {
  const policy = pricing.policy;
  if (!policy?.pricingMode) return null;

  const isInternal = policy.pricingMode === "paid_internal";
  const quote = pricing.quote;
  if (isInternal && (!pricing.quoteUsable || !quote || quote.unitPriceCents <= 0)) return null;
  const label = isInternal ? null : policy.priceLabel || (policy.pricingMode === "free" ? "Complimentary" : "Paid registration");
  const hasFullPrice = isInternal && pricing.promoActive && quote!.fullPriceCents > quote!.unitPriceCents;

  return (
    <>
      <style>{PRICE_STYLES}</style>
      <div className={`homepage-event-price ${className}`.trim()} data-testid="homepage-event-price" aria-live="polite" aria-label={label ?? "Current ticket price"}>
        {label ? <strong>{label}</strong> : <>
          {hasFullPrice && <s aria-label="Standard price">{formatCents(quote!.fullPriceCents, quote!.currency)}</s>}
          <strong>{formatCents(quote!.unitPriceCents, quote!.currency)}</strong>
          {pricing.promoActive && <span>{quote!.promoEndsAt ? `Promo ends ${new Date(quote!.promoEndsAt).toLocaleDateString()}` : "Current ticket price"}</span>}
        </>}
      </div>
    </>
  );
}
